import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import styles from '../Account.module.css';
import AccountCard from '../AccountCard';
import SectionHead from '../SectionHead';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function IntegrationsSection() {
  const { user, getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [connected, setConnected]   = useState(false);
  // A token row can exist while the token is dead, and a local backend can lack the
  // HubSpot app credentials entirely. Both used to show as a green "Connected".
  const [tokenValid, setTokenValid] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [hubId,     setHubId]       = useState(null);
  const [loading,   setLoading]     = useState(true);
  const [busy,      setBusy]        = useState(false);
  const [msg,       setMsg]         = useState(null); // { type:'ok'|'err', text }

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    try {
      const token = getToken?.();
      const r = await fetch(`${API}/api/hubspot/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.ok) {
        const data = await r.json();
        setConnected(data.connected);
        setHubId(data.hubId);
        // Older backends omit these; absent means "no reason to doubt it".
        setTokenValid(data.tokenValid !== false);
        setConfigured(data.credentialsConfigured !== false);
      }
    } catch {
      // fail silently — integration status is non-critical
    } finally {
      setLoading(false);
    }
  }, [user, getToken]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Handle redirect back from HubSpot OAuth.
  useEffect(() => {
    const hsConnected = searchParams.get('hs_connected');
    const hsError     = searchParams.get('hs_error');
    if (hsConnected === '1') {
      setMsg({ type: 'ok', text: 'HubSpot connected successfully.' });
      fetchStatus();
      setSearchParams({ tab: 'integrations' }, { replace: true });
    } else if (hsError) {
      const labels = {
        access_denied:       'HubSpot connection was cancelled.',
        invalid_state:       'Connection failed — the link expired. Please try again.',
        token_exchange:      'Connection failed — could not exchange tokens. Please try again.',
        hub_already_connected: 'This HubSpot portal is already connected to another RepBuddy account. Please sign out of HubSpot in your browser, log in with your own HubSpot account, and try again.',
      };
      setMsg({ type: 'err', text: labels[hsError] || 'HubSpot connection failed.' });
      setSearchParams({ tab: 'integrations' }, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchStatus]);

  async function handleConnect() {
    setMsg(null);
    setBusy(true);
    try {
      const token = getToken?.();
      const r = await fetch(`${API}/api/hubspot/auth`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error('Could not start HubSpot authorization.');
      const { url } = await r.json();
      window.location.href = url;
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    setMsg(null);
    setBusy(true);
    try {
      const token = getToken?.();
      const r = await fetch(`${API}/api/hubspot/disconnect`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error('Disconnect failed. Please try again.');
      setConnected(false);
      setHubId(null);
      setMsg({ type: 'ok', text: 'HubSpot disconnected. All synced data has been removed.' });
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <SectionHead
        title="Integrations"
        subtitle="Connect external CRM tools to push verified contacts directly from RepBuddy."
      />

      <AccountCard
        title="HubSpot CRM"
        description="Push verified contacts and companies into your HubSpot portal."
        actions={
          loading || !configured ? null : connected ? (
            <button
              className={`${styles.btnDanger} ${styles.btnSmall}`}
              onClick={handleDisconnect}
              disabled={busy}
            >
              {busy ? 'Disconnecting…' : 'Disconnect'}
            </button>
          ) : (
            <button
              className={`${styles.btnPrimary} ${styles.btnSmall}`}
              onClick={handleConnect}
              disabled={busy}
            >
              {busy ? 'Connecting…' : 'Connect HubSpot'}
            </button>
          )
        }
      >
        {loading ? (
          <p className={styles.note}>Checking connection…</p>
        ) : !configured ? (
          <p className={styles.note} style={{ margin: 0, color: '#eab308' }}>
            ● HubSpot is not configured on this server, so nothing can be connected or pushed
            from here. On a local backend, add the HubSpot app credentials to its env file.
          </p>
        ) : connected && !tokenValid ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <p className={styles.note} style={{ margin: 0, color: '#eab308' }}>
              ● Reconnect needed{hubId ? ` · Portal ${hubId}` : ''}
            </p>
            <p className={styles.note} style={{ margin: 0 }}>
              The saved HubSpot token no longer works, so pushes will fail until you disconnect
              and connect again.
            </p>
          </div>
        ) : connected ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <p className={styles.note} style={{ margin: 0, color: '#22c55e' }}>
              ● Connected{hubId ? ` · Portal ${hubId}` : ''}
            </p>
            <p className={styles.note} style={{ margin: 0 }}>
              Use the <strong>Push to HubSpot</strong> button in any Export panel to sync contacts.
            </p>
          </div>
        ) : (
          <p className={styles.note} style={{ margin: 0 }}>
            Not connected. Click <strong>Connect HubSpot</strong> to authorise RepBuddy to
            push contacts into your HubSpot portal.
          </p>
        )}
        {msg && (
          <p className={styles.note} style={{ margin: '10px 0 0', color: msg.type === 'ok' ? '#22c55e' : '#ef4444' }}>
            {msg.text}
          </p>
        )}
      </AccountCard>
    </>
  );
}
